import NotFound from "@/components/NotFound";
import type { RoutePath } from "@/routes/route-types";
import OnboardedRoutesOutlet from "@/components/OnboardedRoutesOutlet";
import OnboardingRoutesOutlet from "@/components/OnboardingRoutesOutlet";
import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter as browserRouter, Navigate, RouteObject } from "react-router-dom";

type BrowserRouterProps = Omit<RouteObject, "path" | "children"> & {
  path?: RoutePath;
  children?: BrowserRouterProps[];
};

const createBrowserRouter = (props: BrowserRouterProps[]) => browserRouter(props as RouteObject[]);

const DashboardPage = lazy(() => import("@/features/dashboard/Dashboard"));
const Component = lazy(() => import("@/features/components/Component"));
const SchedulePage = lazy(() => import("@/features/schedule/SchedulePage"));
const ChatInbox = lazy(() => import("@/features/chat/ChatInbox"));
const ActionCenter = lazy(() => import("@/features/action-center/ActionCenter"));
const AccountsPage = lazy(() => import("@/features/settings/AccountsPage"));
const Login = lazy(() => import("@/features/auth/Login"));

const RouteLoader = () => (
  <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-[#E8E8E8] bg-white text-sm text-[#5A5A5A]">
    Loading...
  </div>
);

const withSuspense = (element: ReactNode) => (
  <Suspense fallback={<RouteLoader />}>{element}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: "/auth",
    element: <OnboardingRoutesOutlet />,
    children: [
      {
        index: true,
        element: <Navigate to="login" replace />,
      },
      {
        path: "login",
        element: withSuspense(<Login />),
      },
    ],
  },
  {
    path: "/",
    element: <OnboardedRoutesOutlet />,
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: "dashboard",
        element: withSuspense(<DashboardPage />),
      },
      {
        path: "components",
        element: withSuspense(<Component />),
      },
      {
        path: "schedule",
        element: withSuspense(<SchedulePage />),
      },
      {
        path: "chat",
        element: withSuspense(<ChatInbox />),
      },
      {
        path: "action-center",
        element: withSuspense(<ActionCenter />),
      },
      {
        path: "settings",
        element: <Navigate to="/settings/accounts" replace />,
      },
      {
        path: "settings/accounts",
        element: withSuspense(<AccountsPage />),
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);
